import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import { TOP_BAR_HEIGHT } from '@aibuild/ui';
import SVG from '@components/1-atoms/SVG';
import SpriteSVG from '@components/1-atoms/SpriteSVG';
import { flexColumn, flexCenterVertical } from '@stylesheets/helpers';
import {
  SIDEBAR_INSET,
  SIDEBAR_MOBILE_BREAKPOINT,
  SIDEBAR_SINGLE_SCROLL_MEDIA,
} from '@constants/sidebar';
import { duration } from '@stylesheets/motion';

const MOBILE_BP = `${SIDEBAR_MOBILE_BREAKPOINT}px`;
const INSET = `${SIDEBAR_INSET}px`;

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${({ theme: { colors } }) => colors.primary};
    outline-offset: 2px;
  }
`;

export const RouterLink = styled(NavLink)`
  ${flexCenterVertical}
  text-decoration: none;
`;

export const Wrapper = styled.div`
  ${flexColumn}
  height: 100%;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      overflow: hidden auto;
      scrollbar-width: none;
    `}

  ${SIDEBAR_SINGLE_SCROLL_MEDIA} {
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

export const Header = styled.div`
  ${flexCenterVertical}
  flex-shrink: 0;
  height: ${TOP_BAR_HEIGHT}px;
  padding: 0 ${INSET};

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      padding-inline: 0;
    `}

  /* Keeps the close button reachable while the single-scroll drawer is scrolled. */
  @media (max-width: ${MOBILE_BP}) {
    position: sticky;
    top: 0;
    z-index: 1;

    ${({ theme: { colors } }) => css`
      background-color: ${colors.surface};
    `}
  }
`;

export const LogoLink = styled(RouterLink)`
  height: 48px;
`;

export const Logo = styled(SVG).attrs(({ theme: { isLightTheme } }) => ({
  name: isLightTheme ? 'logo-white' : 'logo',
}))`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

export const ImgLogo = styled.img`
  max-height: 48px;
  flex-shrink: 0;
`;

export const CollapseSlot = styled.div`
  display: flex;
  margin-left: auto;

  @media (max-width: ${MOBILE_BP}) {
    display: none;
  }
`;

export const Navigation = styled.nav`
  ${flexColumn}
  flex-shrink: 0;
  gap: 3px;
  padding: 23px ${INSET} 0;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      align-items: center;
      gap: 9px;
      padding: 30px 0 0;
    `}
`;

export const NavigationItemIcon = styled(SpriteSVG)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  ${({ theme: { colors } }) => css`
    fill: ${colors.onSurface};
  `}
`;

export const NavigationItemReactIcon = styled.span`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme: { colors } }) => css`
    color: ${colors.onSurface};
  `}
`;

export const NavigationItemLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;

  ${({ theme: { colors, font } }) => css`
    font-family: ${font.labelLarge.fontFamily}, sans-serif;
    color: ${colors.onSurface};
  `}
`;

export const NavigationItemWrapper = styled.div`
  ${flexColumn}
  gap: 2px;
`;

const navigationItemStyle = css`
  ${flexCenterVertical}
  position: relative;
  flex-shrink: 0;
  height: 42px;
  gap: 10px;
  padding: 0 16px;
  ${focusRing}

  ${({ theme: { borderRadius, colors } }) => css`
    border-radius: ${borderRadius.full};
    transition: background-color ${duration.fast} ease;

    &:hover {
      background-color: ${colors.surfaceContainerHigh};
    }

    &.active {
      background-color: ${colors.secondaryContainer};

      ${NavigationItemIcon} {
        fill: ${colors.onSecondaryContainer};
      }

      ${NavigationItemReactIcon}, ${NavigationItemLabel} {
        color: ${colors.onSecondaryContainer};
      }

      ${NavigationItemLabel} {
        font-weight: 700;
      }
    }
  `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      justify-content: center;
      width: 48px;
      height: 48px;
      padding: 0;
    `}
`;

export const CustomNavigationItem = styled.div`
  ${navigationItemStyle}
  cursor: pointer;
`;

export const NavigationItem = styled(RouterLink)`
  ${navigationItemStyle}
`;

export const BottomNavigation = styled(Navigation)`
  margin-top: auto;
  padding-bottom: ${INSET};
`;

export const ExtraContentDivider = styled.hr`
  flex-shrink: 0;
  height: 1px;
  margin: 25px ${INSET} 0 0;
  border: 0;
  background-color: ${({ theme: { colors } }) => colors.shellDivider};
`;

export const TextButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;

  ${({ theme: { colors, font, spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} ${spacing.level5};
    color: ${colors.primary};
    font-size: ${font.labelLarge.fontSize};
    line-height: ${font.labelLarge.lineHeight};
    font-weight: ${font.labelLarge.fontWeight};
    letter-spacing: ${font.labelLarge.letterSpacing};
  `}

  &:hover {
    opacity: 0.8;
  }
`;

export const MobileCloseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;

  ${({ theme: { colors, borderRadius } }) => css`
    padding: 8px;
    color: ${colors.onSurfaceVariant};
    border-radius: ${borderRadius.full};

    &:hover {
      background-color: ${colors.surfaceContainerHigh};
    }
  `}

  @media (max-width: ${MOBILE_BP}) {
    display: flex;
  }
`;
